import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { IconButton, makeStyles, Menu, MenuItem, Tooltip, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router';
import { useHotkeys } from 'react-hotkeys-hook';
import { FormattedMessage, useIntl } from 'react-intl';
import CommentBox, { displayedCommentRoots, sortRootsByUpdatedAt } from '../../../containers/CommentBox/CommentBox';
import {
  ISSUE_TYPE,
  JUSTIFY_TYPE,
  QUESTION_TYPE,
  REPLY_TYPE,
  REPORT_TYPE,
  SUGGEST_CHANGE_TYPE,
  TODO_TYPE
} from '../../../constants/comments';
import Screen from '../../../containers/Screen/Screen';
import { fixName, getMarketInfo } from '../../../utils/userFunctions';
import {
  getFullStage,
  getFurtherWorkStage,
  getInCurrentVotingStage, getInReviewStage, isAcceptedStage
} from '../../../contexts/MarketStagesContext/marketStagesContextHelper';
import { MarketStagesContext } from '../../../contexts/MarketStagesContext/MarketStagesContext';
import EditMarketButton from '../../Dialog/EditMarketButton';
import CardType from '../../../components/CardType';
import { PLACEHOLDER } from '../../../constants/global';
import { stageChangeInvestible } from '../../../api/investibles';
import { allImagesLoaded, invalidEditEvent } from '../../../utils/windowUtils';
import Gravatar from '../../../components/Avatars/Gravatar';
import { useInvestibleVoters } from '../../../utils/votingUtils';
import { getCommenterPresences } from '../../Dialog/Planning/userUtils';
import { getPageReducerPage, usePageStateReducer } from '../../../components/PageState/pageStateHooks';
import { pushMessage } from '../../../utils/MessageBusUtils';
import {
  LOCK_INVESTIBLE,
  LOCK_INVESTIBLE_CHANNEL
} from '../../../contexts/InvestibesContext/investiblesContextMessages';
import { onInvestibleStageChange } from '../../../utils/investibleFunctions';
import { SearchResultsContext } from '../../../contexts/SearchResultsContext/SearchResultsContext';
import {
  APPROVAL_WIZARD_TYPE,
  JOB_COMMENT_WIZARD_TYPE,
  JOB_EDIT_WIZARD_TYPE, JOB_STAGE_WIZARD_TYPE
} from '../../../constants/markets';
import {
  OPERATION_HUB_CHANNEL,
  STOP_OPERATION
} from '../../../contexts/OperationInProgressContext/operationInProgressMessages';
import { GmailTabItem, GmailTabs } from '../../../containers/Tab/Inbox';
import {
  formInvestibleLink, formMarketLink,
  formWizardLink,
  navigate,
  preventDefaultAndProp
} from '../../../utils/marketIdPathFunctions';
import { filterToRoot } from '../../../contexts/CommentsContext/commentsContextHelper';
import { CommentsContext } from '../../../contexts/CommentsContext/CommentsContext';
import { getMarketInvestibles } from '../../../contexts/InvestibesContext/investiblesContextHelper';
import { isAssistanceRespondedByHuman } from '../../../utils/commentFunctions';
import { getStagesInfo } from '../../../utils/stageUtils';
import PlanningInvestibleNav, { useMetaDataStyles } from './PlanningInvestibleNav';
import GravatarAndName from '../../../components/Avatars/GravatarAndName';
import SpinningButton from '../../../components/SpinBlocking/SpinningButton';
import { wizardStyles } from '../../../components/AddNewWizards/WizardStylesContext';
import AddIcon from '@material-ui/icons/Add';
import CondensedTodos, { orderCondensedTasks } from './CondensedTodos';
import NotesTab from './NotesTab';
import { groupCapsulesByTarget, groupNotesByDay } from './notesGrouping';
import { getBrowserTz } from '../../../utils/timezoneUtils';
import { DoneAll, ExpandLess } from '@material-ui/icons';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DescriptionOrDiff from '../../../components/Descriptions/DescriptionOrDiff';
import { useInvestibleEditStyles } from '../InvestibleBodyEdit';
import { setUclusionLocalStorageItem } from '../../../components/localStorageUtils';
import DismissableText from '../../../components/Notifications/DismissableText';
import { useGroupPresences } from '../../../contexts/MarketPresencesContext/marketPresencesHelper';
import { MarketPresencesContext } from '../../../contexts/MarketPresencesContext/MarketPresencesContext';
import EditIcon from '@material-ui/icons/Edit';
import { hasJobComment } from '../../../components/AddNewWizards/JobComment/AddCommentStep';
import InlineWizardHost from '../../../components/InlineWizard/InlineWizardHost';
import { InlineWizardLaunchContext } from '../../../components/InlineWizard/InlineWizardContext';
import Link from '@material-ui/core/Link';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import AssignmentOutlined from '@material-ui/icons/AssignmentOutlined';
import ContactSupportOutlined from '@material-ui/icons/ContactSupportOutlined';
import NoteOutlined from '@material-ui/icons/NoteOutlined';
import { InvestiblesContext } from '../../../contexts/InvestibesContext/InvestiblesContext';
import { OperationInProgressContext } from '../../../contexts/OperationInProgressContext/OperationInProgressContext';
import TooltipIconButton from '../../../components/Buttons/TooltipIconButton';
import { NotificationsContext } from '../../../contexts/NotificationsContext/NotificationsContext';
import { findMessagesForCommentIds, findMessagesForInvestibleId,
  findMessagesForInvestibleIds } from '../../../utils/messageUtils';
import { deleteOrDehilightMessages } from '../../../api/users';
import { isInInbox } from '../../../contexts/NotificationsContext/notificationsContextHelper';
import { DARK_ACTION_BUTTON_COLOR, DARK_TEXT_BACKGROUND_COLOR, useButtonColors } from '../../../components/Buttons/ButtonConstants';
import { ThemeModeContext } from '../../../contexts/ThemeModeContext';
import { assistanceNewMessages, countAssistanceRootsWithNewMessages } from './assistanceNotificationCounts';
import { useOpenChipNotification } from '../../../utils/openChipNotification';
import { useSearchMatchNavigation } from '../../../utils/searchMatchNavigation';
import { useSyncedMessages } from '../../../contexts/SyncedMessagesContext/SyncedMessagesContext';
import NotificationMenuButton from '../../../components/Buttons/NotificationMenuButton';
import Approvals from './Approvals';

export const usePlanningInvestibleStyles = makeStyles(
  theme => ({
    root: {
      alignItems: "flex-start",
      display: "flex"
    },
    container: {
      padding: "3px 89px 21px 21px",
      marginTop: "-6px",
      boxShadow: "none",
      [theme.breakpoints.down("sm")]: {
        padding: "3px 21px 42px 21px"
      }
    },
    explain: {
      fontSize: 12,
    },
    content: {
      fontSize: "15 !important",
      lineHeight: "175%",
      color: "#414141",
      [theme.breakpoints.down("sm")]: {
        fontSize: 13
      },
      "& > .ql-container": {
        fontSize: "15 !important"
      }
    },
    cardType: {
      display: "inline-flex"
    },
    mobileColumn: {
      overflowWrap: "break-word",
      [theme.breakpoints.down("sm")]: {
        flexDirection: 'column'
      }
    },
    editCardContent: {
      margin: theme.spacing(2, 1, 2, 2),
      padding: 0,
      '& img': {
        margin: '.75rem 0',
      },
      [theme.breakpoints.down("sm")]: {
        margin: 0,
        padding: '15px'
      }
    },
    votingCardContent: {
      margin: theme.spacing(0, 2, 2, 0),
      padding: 0,
      '& img': {
        margin: '.75rem 0',
      },
      [theme.breakpoints.down("sm")]: {
        margin: 0,
        padding: '15px'
      }
    },
    actions: {},
    blue: {
      backgroundColor: '#2d9cdb',
    },
    upperRightCard: {
      display: "inline-flex",
      float: "right",
      padding: 0,
      margin: 0,
    },
    borderRight: {
      [theme.breakpoints.down("sm")]: {
        padding: '1rem 0',
        marginTop: '1rem',
        borderRight: 'none',
        borderBottom: '1px solid #e0e0e0',
        flexGrow: 'unset',
        maxWidth: 'unset',
        flexBasis: 'auto'
      }
    },
    borderLeft: {
      paddingLeft: '1rem',
      marginTop: '-1.5rem',
      [theme.breakpoints.down("sm")]: {
        paddingLeft: 'unset',
        paddingRight: 0,
        marginTop: '1rem',
        borderLeft: 'none',
        flexGrow: 'unset',
        maxWidth: 'unset',
        marginRight: 'unset',
        flexBasis: 'auto'
      }
    },
    editRow: {
      display: "flex",
      // C-all-1019: top-align the pencil with the card type header (the old fixed height +
      // top nudge were tuned for the mobile-only button and left it sitting too low on desktop).
      alignItems: 'flex-start',
      marginLeft: '0.5rem'
    },
    fullWidthCentered: {
      justifyContent: 'center',
      display: "flex",
      [theme.breakpoints.down("sm")]: {
        maxWidth: '100%',
        flexBasis: '100%',
        flexDirection: 'column'
      }
    },
    autocompleteContainer: {
      display: 'flex',
      paddingBottom: '1.5rem',
      flexDirection: 'column',
    },
    labelChip: {
      paddingRight: 0,
      paddingTop: 'unset',
      maxHeight: 'unset',
      paddingBottom: '5px'
    },
    labelExplain: {
      marginLeft: '10px',
      width: 90,
      [theme.breakpoints.down("sm")]: {
        width: 'auto',
      }
    },
    fullWidthEditable: {
      // T-all-2215: plain hand (pointer) on hover instead of the pencil cursor.
      cursor: 'pointer',
        maxWidth: '100%',
        flexBasis: '100%',
        paddingLeft: 'unset',
        borderLeft: 'none',
        marginLeft: 'unset'
      },
    fullWidth: {
      paddingRight: '1rem',
        maxWidth: '100%',
        flexBasis: '100%',
        paddingLeft: 'unset',
        borderLeft: 'none',
        marginLeft: 'unset'
    },
    datePicker: {
      position: 'absolute',
      zIndex: 1000
    },
    assignments: {
      padding: 0,
      "& ul": {
        flex: 4,
        margin: 0,
        padding: 0
      },
      "& li": {
        display: "inline-flex",
        marginLeft: theme.spacing(1)
      }
    },
    assignmentContainer: {
      width: '100%',
      maxWidth: '11rem'
    },
    assignIconContainer: {
      display: 'flex',
      justifyContent: 'center'
    },
    assignmentFlexRow: {
      width: '100%',
      maxWidth: '20rem',
      paddingTop: '0.5rem'
    },
    paper: {
      // See https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/Drawer/Drawer.js
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: "flex-start",
      '& > div': {
        borderRadius: '6px',
        marginBottom: '1.5rem',
        flexShrink: 0
      },
      flex: '1 0 auto',
      backgroundColor: theme.palette.background.paper,
      height: 'calc(100vh - 3.5rem)',
      zIndex: 9,
      position: 'fixed',
      top: '3.5rem',
      paddingLeft: '1rem',
      minWidth: '15rem',
      textOverflow: 'ellipsis',
    },
    mobileDetails: {
      overflowY: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: "flex-start",
      '& > div': {
        borderRadius: '6px',
        marginBottom: '1rem'
      },
      flex: '1 0 auto',
      minWidth: '15rem',
      textOverflow: 'ellipsis',
    },
    group: {
      borderRadius: 6,
      display: "flex",
      flexDirection: "row",
      "&:first-child": {
        marginLeft: 0
      },
    },
    sectionTitle: {
      fontWeight: 700
    },
    largeGravatar: {
      width: '45px',
      height: '45px',
    },
    smallGravatar: {
      width: '30px',
      height: '30px',
    }
  }),
  { name: "PlanningInvestible" }
);

export function useCollaborators(marketPresences, investibleComments, marketPresencesState, investibleId, marketId,
  returnPresences=false) {
  const investibleCommentorPresences = getCommenterPresences(marketPresences, investibleComments,
    marketPresencesState);
  const voters = useInvestibleVoters(marketPresences, investibleId, marketId, true);
  const concated = [...voters, ...investibleCommentorPresences];
  const presences = _.uniqBy(concated || [], 'id');
  if (returnPresences) {
    return presences;
  }
  return presences.map((presence) => presence.id);
}

export function countUnresolved(comments, search) {
  if (_.isEmpty(comments)) {
    return undefined;
  }
  let unresolvedComments;
  if (!_.isEmpty(search)) {
    unresolvedComments =  comments;
  } else {
    unresolvedComments = comments.filter((comment) => !comment.resolved);
  }
  return _.isEmpty(unresolvedComments) ? undefined : `${_.size(unresolvedComments)}`;
}

/**
 * A page that represents what the investible looks like for a DECISION Dialog
 * @param props
 * @constructor
 */
function PlanningInvestible(props) {
  const history = useHistory();
  const openChipNotification = useOpenChipNotification();
  const { firstCommentMatch, openMatch } = useSearchMatchNavigation();
  const location = useLocation();
  const intl = useIntl();
  const {
    investibleId,
    marketPresences = [],
    investibleComments = [],
    userId,
    marketId,
    marketInvestible,
    investibles = [],
    hash,
    inArchives=false,
    market,
    hidden = false,
    unSentInvestibleComments = []
  } = props;
  const [themeMode] = React.useContext(ThemeModeContext);
  const isDark = themeMode === 'dark';
  const theme = useTheme();
  const editorBox = useRef(null);
  const mobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const { warningColor } = useButtonColors();
  const leftNavBreak = useMediaQuery(theme.breakpoints.down('md'));
  const refToTop = useRef();
  const classes = usePlanningInvestibleStyles();
  const editClasses = useInvestibleEditStyles();
  const wizardClasses = wizardStyles();
  const [searchResults] = useContext(SearchResultsContext);
  const [investiblesState, investiblesDispatch] = useContext(InvestiblesContext);
  const [messagesState, messagesDispatch] = useContext(NotificationsContext);
  const syncedMessages = useSyncedMessages();
  const [commentsState] = useContext(CommentsContext);
  const [allDoneAnchorEl, setAllDoneAnchorEl] = useState(null);
  const [, setOperationRunning] = useContext(OperationInProgressContext);
  const [marketPresencesState] = useContext(MarketPresencesContext);
  const { results, parentResults, search } = searchResults;
  const investibleCommentsSearched = investibleComments.filter((comment) => {
    if (_.isEmpty(search)) {
      return true;
    }
    return results.find((item) => item.id === comment.id) || parentResults.find((id) => id === comment.id);
  });
  const investmentReasonsSearched = investibleCommentsSearched.filter((comment) => {
      return comment.comment_type === JUSTIFY_TYPE;
  });
  const marketInfo = getMarketInfo(marketInvestible, marketId) || {};
  const { stage, assigned: invAssigned, ticket_code: ticketCode, former_stage_id: formerStageId, group_id: groupId,
    created_by: createdById } = marketInfo;
  const assigned = invAssigned || [];
  const { investible } = marketInvestible;
  const { name, description, locked_by: lockedBy, created_at: createdAt, labels } = investible;
  const [marketStagesState] = useContext(MarketStagesContext);
  const inCurrentVotingStage = getInCurrentVotingStage(marketStagesState, marketId);
  const isInVoting = Boolean(inCurrentVotingStage && stage === inCurrentVotingStage.id);
  const [detailsOpen, setDetailsOpen] = useState(mobileLayout);
  const reportsCommentsSearched = investibleCommentsSearched.filter(
    comment => comment.comment_type === REPORT_TYPE && comment.notification_type !== 'BLUE'
  );
  const fullStage = getFullStage(marketStagesState, marketId, stage) || {};
  const [pageStateFull, pageDispatch] = usePageStateReducer('investible');
  const isAssigned = assigned.includes(userId);
  const [pageState, updatePageState] = getPageReducerPage(pageStateFull, pageDispatch, investibleId,
    {reportsOpenRaw: true, sectionOpen: fullStage.move_on_comment ? 'assistanceSection' :
        (isAcceptedStage(fullStage) && isAssigned ? 'tasksSection' : 'descriptionVotingSection')});
  const {
    sectionOpen,
    compressionHash,
    showDiff,
    reportsOpenRaw,
    assistanceTabIndex
  } = pageState;
  // T-all-2298: which Debatable sub-tab (0 Unresponded, 1 Responded, 2 Resolved) is open
  const assistanceTab = assistanceTabIndex || 0;
  const reportsOpen = !_.isEmpty(search) ? !_.isEmpty(reportsCommentsSearched) : reportsOpenRaw;
  // J-all-325: the add wizards open inside this container instead of on the full-screen /wizard route.
  // The open-state is kept local (not in the URL) so that navigating away and back shows the page as
  // it normally displays (T-all-2188). Any navigation - including the post-submit navigate to the new
  // item (Q-all-147) - clears it via the effect below.
  const [inlineWizard, setInlineWizard] = useState(undefined);
  // T-all-2448: stable identities so the inline wizard context values don't force every
  // consumer (including a live editor's owner) to re-render on each page render
  const openInlineWizard = useCallback((descriptor) => {
    setInlineWizard(descriptor);
  }, []);
  const closeInlineWizard = useCallback(() => {
    setInlineWizard(undefined);
  }, []);
  const inlineWizardLaunchValue = useMemo(() => ({ openInlineWizard, inlineWizard }),
    [openInlineWizard, inlineWizard]);
  useEffect(() => {
    // Clear the inline wizard whenever the location changes (navigating away, or the wizard navigating
    // to the item it just created). Opening the wizard does not change the URL, so it is unaffected.
    setInlineWizard(undefined);
  }, [location.pathname, location.hash]);
  useHotkeys('ctrl+a', () => openInlineWizard({ wizardType: JOB_COMMENT_WIZARD_TYPE, investibleId, marketId,
    commentType: TODO_TYPE }), { enabled: !hidden && !inlineWizard }, [investibleId, marketId, hidden, inlineWizard]);
  useHotkeys('ctrl+q', () => openInlineWizard({ wizardType: JOB_COMMENT_WIZARD_TYPE, investibleId, marketId,
    commentType: QUESTION_TYPE }), { enabled: !hidden && !inlineWizard }, [investibleId, marketId, hidden, inlineWizard]);
  useHotkeys('ctrl+alt+s', () => openInlineWizard({ wizardType: JOB_COMMENT_WIZARD_TYPE, investibleId, marketId,
    commentType: SUGGEST_CHANGE_TYPE }), { enabled: !hidden && !inlineWizard }, [investibleId, marketId, hidden, inlineWizard]);
  useHotkeys('ctrl+alt+b', () => openInlineWizard({ wizardType: JOB_COMMENT_WIZARD_TYPE, investibleId, marketId,
    commentType: ISSUE_TYPE }), { enabled: !hidden && !inlineWizard }, [investibleId, marketId, hidden, inlineWizard]);
  useHotkeys('ctrl+alt+n', () => openInlineWizard({ wizardType: JOB_COMMENT_WIZARD_TYPE, investibleId, marketId,
    commentType: REPORT_TYPE, notificationType: 'BLUE' }), { enabled: !hidden && !inlineWizard },
    [investibleId, marketId, hidden, inlineWizard]);
  const isMac = window.navigator.userAgentData ? window.navigator.userAgentData.platform === 'macOS'
    : /Mac/i.test(window.navigator.userAgent);
  useHotkeys(isMac ? 'ctrl+option+arrowDown' : 'ctrl+arrowDown', () => {
    updatePageState({ sectionOpen: 'assistanceSection', assistanceTabIndex: 0 });
  }, { enabled: !hidden && !inlineWizard, enableOnContentEditable: true },
    [hidden, inlineWizard, updatePageState]);
  const showJobHeaderAboveWizard = !!inlineWizard && (inlineWizard.wizardType === APPROVAL_WIZARD_TYPE ||
    (inlineWizard.wizardType === JOB_COMMENT_WIZARD_TYPE && inlineWizard.commentType === REPORT_TYPE &&
      inlineWizard.notificationType !== 'BLUE'));
  const canVote = isInVoting && !inArchives;
  const yourPresence = marketPresences.find((presence) => presence.current_user);
  const createdBy = marketPresences.find((presence) => presence.id === createdById) || {};
  const lastStageChangeDate = new Date(marketInfo.last_stage_change_date);
  const investmentExpirationMs = (market?.investment_expiration ?? 0) * 86400000;
  const yourVote = yourPresence?.investments?.find((investment) => {
    if (investment.investible_id !== investibleId || investment.deleted) {
      return false;
    }
    const updatedAtDate = new Date(investment.updated_at);
    const lastEventTime = Math.max(lastStageChangeDate.getTime(), updatedAtDate.getTime());
    const isExpired = Date.now() - lastEventTime > investmentExpirationMs;
    return !isExpired;
  });
  const displayVotingInput = canVote && _.isEmpty(search) && !yourVote;
  const isSingleUser = useGroupPresences(groupId, marketId, marketPresences);
  const collaboratorIds = useCollaborators(marketPresences, investibleComments, marketPresencesState,
    investibleId, marketId);
  const isCollaborator = collaboratorIds.includes(userId);

  const anchorTarget = location.state?.searchMatch || location.state?.notification;
  useEffect(() => {
    // T-all-2298: pick the Debatable sub-tab holding this root so a followed link lands on it
    function assistanceTabFor(rootComment) {
      if (rootComment.resolved) {
        return 2;
      }
      return isAssistanceRespondedByHuman(rootComment, investibleComments, marketPresences,
        marketPresencesState, commentsState) ? 1 : 0;
    }
    function openAssistance(rootComment, extraState) {
      const tabForComment = assistanceTabFor(rootComment);
      if (sectionOpen !== 'assistanceSection' || assistanceTab !== tabForComment || extraState) {
        updatePageState({ sectionOpen: 'assistanceSection', assistanceTabIndex: tabForComment, ...(extraState || {}) });
      }
    }
    if (hash && hash.length > 1 && !hidden && !hash.includes('header')) {
      const element = document.getElementById(hash.substring(1, hash.length));
      // Check if already on the right tab and only change tab if not
      if (!element) {
        const inlineQuestion = investibleComments.find((comment) =>
          comment.inline_market_id && comment.inline_market_id === anchorTarget?.marketId);
        if (inlineQuestion) {
          openAssistance(inlineQuestion, compressionHash?.[inlineQuestion.id] === false ? undefined :
            { compressionHash: { ...compressionHash, [inlineQuestion.id]: false } });
        } else if (hash.startsWith('#cv') || hash.startsWith('#approve')) {
          updatePageState({ sectionOpen: 'descriptionVotingSection' });
        } else if (hash.startsWith('#start')) {
          updatePageState({ sectionOpen: 'tasksSection' });
          history.replace(window.location.pathname + window.location.search);
        } else if (hash.startsWith('#option')) {
          const questionComment = investibleComments.find((comment) =>
            comment.comment_type === QUESTION_TYPE && comment.inline_market_id &&
            (getMarketInvestibles(investiblesState, comment.inline_market_id) || [])
              .find((inv) => hash.includes(inv.investible.id)));
          if (questionComment) {
            openAssistance(questionComment);
            // Scroll context should send to the option now
          } else if (sectionOpen !== 'assistanceSection') {
            updatePageState({ sectionOpen: 'assistanceSection' });
          }
        } else if (hash.startsWith('#investibleCondensedTodos')) {
          updatePageState({ sectionOpen: 'descriptionVotingSection' });
        } else {
          const found = investibleComments.find((comment) => hash.includes(comment.id));
          if (!_.isEmpty(found)) {
            const rootComment = filterToRoot(investibleComments, found.id);
            if (!_.isEmpty(rootComment.investible_id)) {
              switch (rootComment.comment_type) {
                case JUSTIFY_TYPE:
                  if (sectionOpen !== 'descriptionVotingSection') {
                    updatePageState({ sectionOpen: 'descriptionVotingSection' });
                  }
                  break;
                case REPORT_TYPE:
                  if (rootComment.notification_type === 'BLUE') {
                    if (sectionOpen !== 'notesSection') {
                      updatePageState({ sectionOpen: 'notesSection' });
                    }
                    break;
                  } else {
                    if (sectionOpen !== 'descriptionVotingSection' || !reportsOpenRaw) {
                      updatePageState({ reportsOpenRaw: true, sectionOpen: 'descriptionVotingSection' });
                    }
                    break;
                  }
                case TODO_TYPE:
                  if (rootComment.resolved) {
                    if (sectionOpen !== 'descriptionVotingSection') {
                      updatePageState({ sectionOpen: 'descriptionVotingSection' });
                    }
                    break;
                  }
                  if (sectionOpen !== 'tasksSection') {
                    updatePageState({ sectionOpen: 'tasksSection' });
                  }
                  break;
                case ISSUE_TYPE:
                case SUGGEST_CHANGE_TYPE:
                case QUESTION_TYPE:
                  if (found.comment_type === REPLY_TYPE) {
                    let newCompressionHash = undefined;
                    if (!_.isEmpty(compressionHash)) {
                      if (compressionHash[rootComment.id] !== false) {
                        newCompressionHash = { ...compressionHash, [rootComment.id]: false };
                      }
                    } else {
                      newCompressionHash = { [rootComment.id]: false };
                    }
                    openAssistance(rootComment, newCompressionHash ?
                      { compressionHash: newCompressionHash } : undefined);
                  } else {
                    openAssistance(rootComment);
                  }
                  break;
                default:
              }
            }
          }
        }
      }
    }
  }, [investibleComments, hash, sectionOpen, updatePageState, hidden, history, compressionHash, reportsOpenRaw,
    assistanceTab, investiblesState, marketPresences, marketPresencesState, commentsState, anchorTarget?.marketId]);

  let lockedByName
  if (lockedBy) {
    const lockedByPresence = marketPresences.find(
      presence => presence.id === lockedBy
    );
    if (lockedByPresence) {
      const { name } = lockedByPresence;
      lockedByName = name;
    }
  }
  const stagesInfo = getStagesInfo(marketId, marketStagesState, stage);
  const {
    isInReview,
    isInAccepted,
    isInBlocked,
    isRequiresInput,
    isInNotDoing,
  } = stagesInfo;

  const myPresence = marketPresences.find((presence) => presence.current_user) || {};
  const inMarketArchives = isInNotDoing || isInReview;
  const blockingCommentsUnresolved = investibleComments.filter(
    comment => comment.comment_type === ISSUE_TYPE && !comment.resolved
  );
  function canGetInput() {
    return _.isEmpty(blockingCommentsUnresolved) && !isInReview && !isInNotDoing;
  }
  const suggestionComments = investibleComments.filter(
    comment => comment.comment_type === SUGGEST_CHANGE_TYPE
  );
  const questionComments = investibleComments.filter(
    comment => comment.comment_type === QUESTION_TYPE
  );
  const questionsOrSuggestionsComments = questionComments.concat(suggestionComments);
  const questionSuggestionsByAssignedComments = questionsOrSuggestionsComments.filter(
    comment => !comment.resolved && assigned.includes(comment.created_by)
  );
  function canOpenBlocking() {
    return _.isEmpty(questionSuggestionsByAssignedComments) && !isInReview && !isInNotDoing;
  }
  const unresolvedComments = investibleComments.filter(comment => !comment.resolved);
  const mustResolveComments = unresolvedComments.filter((comment) =>
    [ISSUE_TYPE, TODO_TYPE].includes(comment.comment_type)||
    ([QUESTION_TYPE, SUGGEST_CHANGE_TYPE].includes(comment.comment_type) &&
      (assigned || []).includes(comment.created_by)));
  const todoCommentsSearched = investibleCommentsSearched.filter(
    comment => comment.comment_type === TODO_TYPE
  );
  const openTodoCommentsSearched = todoCommentsSearched.filter((comment) => !comment.resolved);
  const openTodoCommentsRepliesSearched = investibleComments.filter((comment) => comment.comment_type === REPLY_TYPE &&
    openTodoCommentsSearched.some((parent) => parent.id === comment.root_comment_id));
  const openTodoCommentsSearchedAll = openTodoCommentsSearched.concat(openTodoCommentsRepliesSearched);
  const todoCommentsResolvedSearched = todoCommentsSearched.filter((comment) => comment.resolved);
  const questionCommentsSearched = investibleCommentsSearched.filter(
    comment => comment.comment_type === QUESTION_TYPE
  );
  const suggestionCommentsSearched = investibleCommentsSearched.filter(
    comment => comment.comment_type === SUGGEST_CHANGE_TYPE
  );
  const blockingCommentsSearched = investibleCommentsSearched.filter(
    comment => comment.comment_type === ISSUE_TYPE
  );
  const assistanceCommentsSearched = questionCommentsSearched.concat(suggestionCommentsSearched)
    .concat(blockingCommentsSearched);
  // T-all-2298: split Debatable into Unresponded, Responded (a human added something later than
  // the AI), and Resolved
  const resolvedAssistanceComments = sortRootsByUpdatedAt(
    assistanceCommentsSearched.filter((comment) => comment.resolved));
  const unresolvedAssistanceComments = assistanceCommentsSearched.filter((comment) => !comment.resolved);
  const respondedAssistanceComments = unresolvedAssistanceComments.filter((comment) =>
    isAssistanceRespondedByHuman(comment, investibleComments, marketPresences, marketPresencesState, commentsState));
  const unrespondedAssistanceComments = unresolvedAssistanceComments.filter((comment) =>
    !respondedAssistanceComments.includes(comment));
  const assistanceTabComments = [unrespondedAssistanceComments, respondedAssistanceComments,
    resolvedAssistanceComments];
  const capsuleNotes = investibleCommentsSearched.filter(
    comment => !comment.reply_id && comment.comment_type === REPORT_TYPE &&
      comment.notification_type === 'BLUE' && comment.pinned === true
  );
  const notesCommentsSearched = investibleCommentsSearched.filter(
    comment => comment.comment_type === REPORT_TYPE && comment.notification_type === 'BLUE' &&
      comment.pinned !== true
  );
  const displayedNoteRoots = notesCommentsSearched.concat(capsuleNotes);
  const notesCommentsRepliesSearched = investibleComments.filter((comment) => comment.comment_type === REPLY_TYPE &&
    displayedNoteRoots.some((parent) => parent.id === comment.root_comment_id));
  const notesCommentsAllSearched = displayedNoteRoots.concat(notesCommentsRepliesSearched);
  const assistanceCommentsRepliesSearched = investibleComments.filter((comment) => comment.comment_type === REPLY_TYPE &&
    assistanceCommentsSearched.some((parent) => parent.id === comment.root_comment_id));
  const assistanceCommentsSearchedAll = assistanceCommentsSearched.concat(assistanceCommentsRepliesSearched);
  // J-all-440: every tab count on this page reads the synced set. jobInboxMessages just below
  // deliberately does not, because All Done must still be able to clear an unsynced message.
  const newAssistanceMessages = findMessagesForCommentIds(assistanceCommentsSearchedAll?.map((comment) => comment.id), 
    syncedMessages, true);
  const newInvestibleMessages = findMessagesForInvestibleIds([investibleId], syncedMessages, true);
  // T-all-2439: everything in this user's inbox about the job - offered for clearing on All Done
  const jobInboxMessages = findMessagesForInvestibleId(investibleId, messagesState.messages)
    .filter((message) => isInInbox(message));

  function moveJobToReviewable(doClear) {
    const inReviewStageId = getInReviewStage(marketStagesState, marketId).id;
    // C-all-2248: nothing left to ask once open tasks are already closed, so move directly.
    if (_.isEmpty(mustResolveComments)) {
      // Menu choices do not use SpinningButton's automatic lock, while the no-notifications path does.
      // Taking ownership here covers both without locking the wizard-only branches below.
      setOperationRunning('allDone');
      const moveInfo = {
        marketId,
        investibleId,
        stageInfo: {
          current_stage_id: stage,
          stage_id: inReviewStageId
        },
      };
      return stageChangeInvestible(moveInfo)
        .then((newInv) => {
          onInvestibleStageChange(inReviewStageId, newInv, investibleId, marketId,
            undefined, undefined, investiblesDispatch,
            () => {}, marketStagesState, undefined, fullStage);
          const clearPromise = doClear ? deleteOrDehilightMessages(
            jobInboxMessages, messagesDispatch, true, false, true)
            : Promise.resolve(true);
          return clearPromise.then(() => {
            navigate(history, formMarketLink(marketId, groupId));
          });
        })
        .finally(() => setOperationRunning(false));
    }
    const clearSuffix = doClear ? '&clearNotifications=true' : '';
    navigate(history, `${formWizardLink(JOB_STAGE_WIZARD_TYPE, marketId,
      investibleId)}&stageId=${inReviewStageId}${clearSuffix}`);
  }
  newInvestibleMessages.forEach((message) => {
    if (message.market_id !== marketId) {
      // If message is part of a different market must be assistance
      newAssistanceMessages.push(message);
    }
  });
  // T-all-2475: the Debatable tab counts roots needing attention exactly like its Unresponded
  // sub-tab instead of one per notification, so replies and votes on one thread stay one new
  const numNewAssistanceMessages = countAssistanceRootsWithNewMessages(
    assistanceCommentsSearched, investibleComments, syncedMessages);
  // T-all-2308: per sub-tab notification counts - same rule as the sections, a sub-tab shows its
  // new message count instead of its contents count when new messages are present
  function countBucketNewMessages(bucketComments) {
    const bucketAll = bucketComments.concat(investibleComments.filter((comment) =>
      comment.comment_type === REPLY_TYPE &&
      bucketComments.some((parent) => parent.id === comment.root_comment_id)));
    const bucketMessages = findMessagesForCommentIds(bucketAll.map((comment) => comment.id),
      syncedMessages, true);
    newInvestibleMessages.forEach((message) => {
      if (message.market_id !== marketId &&
        bucketComments.find((comment) => comment.inline_market_id === message.market_id)) {
        bucketMessages.push(message);
      }
    });
    return bucketMessages.filter((message) => isInInbox(message));
  }
  const assistanceTabNewMessageSets = assistanceTabComments.map((bucket, index) => index === 0 ?
    assistanceNewMessages(bucket, investibleComments, syncedMessages) : countBucketNewMessages(bucket));
  const assistanceTabNewCounts = assistanceTabComments.map((bucket, index) => index === 0 ?
    countAssistanceRootsWithNewMessages(bucket, investibleComments, syncedMessages) :
    _.size(assistanceTabNewMessageSets[index]));
  const newTodoMessages = findMessagesForCommentIds(openTodoCommentsSearchedAll?.map((comment) => comment.id),
    syncedMessages, true);
  // Do not include each unread task as its own message
  const inboxNewTodoMessages = newTodoMessages.filter((message) => isInInbox(message));
  const numNewTodoMessages = _.size(inboxNewTodoMessages);
  const newNotesMessages = findMessagesForCommentIds(notesCommentsAllSearched?.map((comment) => comment.id), 
  syncedMessages, true);
  const inboxNewNotesMessages = newNotesMessages.filter((message) => isInInbox(message));
  const newNotOverviewMessages = newTodoMessages.concat(newAssistanceMessages).concat(newNotesMessages);
  const newOverviewMessages = newInvestibleMessages.filter((message) => isInInbox(message) && 
    !newNotOverviewMessages.includes(message));
  const numNewOverviewMessages = _.size(newOverviewMessages);
  const numNewNotesMessages = _.size(inboxNewNotesMessages);
  const assistanceChipMessages = assistanceNewMessages(assistanceCommentsSearched, investibleComments, syncedMessages);
  const replies = investibleComments.filter((comment => comment.comment_type === REPLY_TYPE));
  let allowedCommentTypes = [];
  let sectionComments = [];
  if (sectionOpen === 'tasksSection') {
    allowedCommentTypes = [TODO_TYPE];
    sectionComments = openTodoCommentsSearched;
  } else if (sectionOpen === 'assistanceSection') {
    if (canGetInput()) {
      allowedCommentTypes = [QUESTION_TYPE, SUGGEST_CHANGE_TYPE];
    }
    if (canOpenBlocking()) {
      allowedCommentTypes.push(ISSUE_TYPE);
    }
    sectionComments = assistanceTabComments[assistanceTab] || [];
  } else if (sectionOpen === 'notesSection') {
    allowedCommentTypes = [REPORT_TYPE];
    sectionComments = notesCommentsSearched;
  }

  function isEditableByUser() {
    const imagesLoaded = allImagesLoaded(editorBox?.current)
    return imagesLoaded && !inArchives;
  }

  function mySetBeingEdited(event) {
    if (!isEditableByUser() || invalidEditEvent(event)) {
      return;
    }
    const needsLock = !((isInReview || isInAccepted) && _.size(assigned) === 1) && lockedBy !== myPresence?.id;
    // Do not attempt to grab the lock from someone else here - have wizard for that
    if (needsLock && _.isEmpty(lockedBy)) {
      pushMessage(LOCK_INVESTIBLE_CHANNEL, { event: LOCK_INVESTIBLE, marketId, investibleId });
    }
    setUclusionLocalStorageItem(`name-editor-${investibleId}`, name);
    // T-all-2215: open the job edit wizard inline instead of on the full-screen /wizard route.
    openInlineWizard({ wizardType: JOB_EDIT_WIZARD_TYPE, marketId, investibleId });
  }

  function openSubSection(subSection) {
    updatePageState({sectionOpen: subSection});
  }

  const countReports =  _.size(reportsCommentsSearched);
  const title = ticketCode ? `${ticketCode} ${name}` : name;
  const descriptionSectionResults = _.isEmpty(search) ? 0 :
      ((results || []).find((item) => item.id === investibleId) ? 1 : 0) + _.size(investmentReasonsSearched)
    + countReports + _.size(todoCommentsResolvedSearched);
  const displayQuestionSection = canGetInput() || _.size(questionCommentsSearched) > 0;
  const displaySuggestionsSection = canGetInput() || _.size(suggestionCommentsSearched) > 0;
  const displayBockingSection = canOpenBlocking() || _.size(blockingCommentsSearched) > 0;
  const sections = ['descriptionVotingSection', 'tasksSection'];
  const displayAssistanceSection = displayQuestionSection || displaySuggestionsSection || displayBockingSection;
  if (displayAssistanceSection) {
    sections.push('assistanceSection');
  }
  sections.push('notesSection');
  function getTagLabel(tagLabelId) {
    if (!_.isEmpty(search)) {
      return intl.formatMessage({ id: 'match' });
    }
    return intl.formatMessage({ id: tagLabelId });
  }
  const showCommentAdd = !inArchives && !isInNotDoing && _.isEmpty(search) && marketId &&
    !_.isEmpty(investible) && !hidden;
  const investibleNav = <PlanningInvestibleNav key={investibleId} investibles={investibles} name={name}
                                               marketInvestible={marketInvestible} classes={classes}
                                               investibleId={investibleId} yourVote={yourVote}
                                               userId={userId} myPresence={myPresence} isAssigned={isAssigned}
                                               pageState={pageState} marketPresences={marketPresences}
                                               assigned={assigned} isInVoting={isInVoting}
                                               investibleComments={investibleComments} labels={labels}
                                               marketInfo={marketInfo} marketId={marketId}
                                               openInlineWizard={openInlineWizard}
                                               updatePageState={updatePageState} />;
  function getUseCompression(commentId) {
    if (compressionHash) {
      const useCompression = compressionHash[commentId];
      if (useCompression !== undefined) {
        return useCompression;
      }
    }
    // Default compression to off as tasks are not compressed except on overview and that page is fine
    // Use find so button not there when no replies
    return investibleComments.find(comment => comment.reply_id === commentId) ? false : undefined;
  }

  function toggleUseCompression(commentId) {
    if (compressionHash) {
      const useCompression = !compressionHash[commentId];
      const newCompressionHash = {...compressionHash, [commentId]: useCompression};
      updatePageState({compressionHash: newCompressionHash});
    } else {
      updatePageState({compressionHash: {[commentId]: false}});
    }
  }

  function toggleDetails() {
    setDetailsOpen(!detailsOpen);
  }

  function toggleReports() {
    updatePageState({reportsOpenRaw: !reportsOpenRaw});
  }

  const assistanceTag = numNewAssistanceMessages > 0 && _.isEmpty(search) ? `${numNewAssistanceMessages}` : 
    countUnresolved(assistanceCommentsSearched, search);

  const hasNewTodoMessages = numNewTodoMessages > 0 && _.isEmpty(search);
  const hasNewAssistanceMessages = numNewAssistanceMessages > 0 && _.isEmpty(search);
  const hasNewOverviewMessages = numNewOverviewMessages > 0 && _.isEmpty(search);
  const hasNewNotesMessages = numNewNotesMessages > 0 && _.isEmpty(search);
  const showOverview = !inlineWizard && sectionOpen === 'descriptionVotingSection';
  const approvalVoters = useInvestibleVoters(marketPresences, investibleId, marketId);
  const searchTargets = { assistanceTabs: [] };
  if (!_.isEmpty(search)) {
    const orderContext = { investiblesState, marketPresencesState, commentsState };
    const ordered = (roots, options = {}) => displayedCommentRoots(roots.concat(replies), searchResults,
      { ...orderContext, ...options });
    const reasonsInOrder = _.sortBy(approvalVoters, 'quantity', 'updatedAt')
      .map((voter) => investmentReasonsSearched.find((reason) => reason.id === voter.commentId)).filter(Boolean);
    searchTargets.overview = results.some((item) => item.id === investibleId)
      ? { url: `${formInvestibleLink(marketId, investibleId)}#investible-header` }
      : firstCommentMatch(orderCondensedTasks(todoCommentsResolvedSearched, investibleComments)
        .concat(reasonsInOrder, ordered(reportsCommentsSearched)));
    searchTargets.tasks = firstCommentMatch(ordered(openTodoCommentsSearched,
      { useInProgressSorting: true, investibleCommentsForSort: investibleComments }));
    searchTargets.assistanceTabs = [
      firstCommentMatch(ordered(unrespondedAssistanceComments, { oldestFirst: true })),
      firstCommentMatch(ordered(respondedAssistanceComments, { simpleOrdering: true })),
      firstCommentMatch(resolvedAssistanceComments),
    ];
    searchTargets.assistance = searchTargets.assistanceTabs.find(Boolean);
    const tasks = investibleComments.filter((comment) => comment.comment_type === TODO_TYPE);
    const days = groupNotesByDay(notesCommentsSearched, tasks, getBrowserTz(), new Set(todoCommentsSearched.map((task) => task.id)));
    const notesInOrder = days.flatMap((day) => day.jobLevelNotes.concat(day.subGroups.flatMap((group) => group.notes)));
    searchTargets.notes = firstCommentMatch(notesInOrder);
    const capsules = groupCapsulesByTarget(capsuleNotes, tasks);
    searchTargets.notesEntry = firstCommentMatch(capsules.jobCapsules
      .concat(capsules.taskGroups.flatMap((group) => group.capsules), notesInOrder));
  }
  function openSearchMatch(target, replace = false) {
    if (target?.url.endsWith('#investible-header')) {
      updatePageState({ sectionOpen: 'descriptionVotingSection' });
    }
    openMatch(target, replace);
  }
  const firstJobMatch = searchTargets.overview || searchTargets.tasks || searchTargets.assistance || searchTargets.notesEntry;
  const searchEntry = useRef();
  useEffect(() => {
    if (hidden) {
      searchEntry.current = undefined;
      return;
    }
    if (searchEntry.current?.job !== investibleId) {
      searchEntry.current = { job: investibleId, handled: false };
    }
    if (searchEntry.current.handled) return;
    if (_.isEmpty(search) || (hash && hash !== '#investible-header')) {
      searchEntry.current.handled = true;
      return;
    }
    if (firstJobMatch) {
      // Hash cleanup and manual tab changes are not another job entry.
      searchEntry.current.handled = true;
      openSearchMatch(firstJobMatch, true);
    }
  });
  const matchClick = (target) => target ? () => openSearchMatch(target) : undefined;
  return (
    <Screen
      title={title}
      tabTitle={name}
      hidden={hidden}
      noPadDesktop
      leftNavAdjust={mobileLayout ? undefined : (leftNavBreak ? 245 : 465)}
    >
      {/* J-all-325 (T-all-2197): expose openInlineWizard to the comment delete buttons so delete opens inline. */}
      <InlineWizardLaunchContext.Provider value={inlineWizardLaunchValue}>
      {!mobileLayout && (
        <div className={classes.paper} style={{ paddingTop: mobileLayout ? undefined : '2rem', paddingBottom: '1rem',
          transform: mobileLayout ? undefined :
          (leftNavBreak ? 'translateX(calc(100vw - 267px))' : 'translateX(calc(100vw - 480px))')}}>
          {investibleNav}
        </div>
      )}
      <GmailTabs
        value={sections.findIndex((section) => section === sectionOpen)}
        addPaddingLeft='2rem' addMarginLeft={leftNavBreak ? '-0.25rem' : undefined}
        onChange={(event, value) => {
          // J-all-325: switching tabs leaves the wizard's context, so close it.
          closeInlineWizard();
          openSubSection(sections[value]);
          // Previous scroll position no longer relevant
          refToTop.current?.scrollIntoView({ block: "end" });
        }}
        id='investible-header'
        // Four entries: with the Assistance tab shown, Notes is the fourth tab and an
        // uncovered index falls back to the treasury styles' black indicator (T-all-2257).
        indicatorColors={['#2F80ED', '#2F80ED', '#2F80ED', '#2F80ED']}
        style={{ paddingBottom: '0.25rem', zIndex: 8, position: mobileLayout ? undefined : 'fixed',
          paddingTop: mobileLayout ? undefined : '0.5rem', width: '100%', marginTop: '-15px', paddingLeft: 0,
          marginLeft: '-0.5rem' }}>
        {/* C-all-994 (Q-all-138, O-4): clean text tabs on desktop; icons only on
           mobile, where the label is blanked for space - refined to outlined icons. */}
        <GmailTabItem icon={mobileLayout ? <InfoOutlined /> : undefined} tagLabel={numNewOverviewMessages > 0 && _.isEmpty(search) ? 'new' : getTagLabel('total')}
                      onTagClick={!_.isEmpty(search) ? matchClick(searchTargets.overview)
                        : (hasNewOverviewMessages ? () => openChipNotification(newOverviewMessages) : undefined)}
                      tagTooltipId={!_.isEmpty(search) ? 'searchGoToMatch' : undefined}
                      label={intl.formatMessage({id: 'descriptionVotingLabel'})}
                      toolTipId='jobOverviewToolTip' tagColor={hasNewOverviewMessages ? warningColor : undefined}
                      tag={descriptionSectionResults === 0 ? 
                          (hasNewOverviewMessages ? `${numNewOverviewMessages}` : undefined) 
                          : `${descriptionSectionResults}`} />
        <GmailTabItem icon={mobileLayout ? <AssignmentOutlined /> : undefined} label={intl.formatMessage({id: 'openTasksSection'})}
                      tagColor={hasNewTodoMessages ? warningColor : undefined}
                      onTagClick={!_.isEmpty(search) ? matchClick(searchTargets.tasks)
                        : (hasNewTodoMessages ? () => openChipNotification(inboxNewTodoMessages) : undefined)}
                      tagTooltipId={!_.isEmpty(search) ? 'searchGoToMatch' : undefined}
                      toolTipId='jobTasksToolTip' tagLabel={hasNewTodoMessages && _.isEmpty(search) ? 'new' : getTagLabel('total')}
                      tag={hasNewTodoMessages && _.isEmpty(search) ? `${numNewTodoMessages}` : countUnresolved(openTodoCommentsSearched, search)} />
        {displayAssistanceSection && (
          <GmailTabItem icon={mobileLayout ? <ContactSupportOutlined /> : undefined} toolTipId='jobAssistanceToolTip'
                        label={intl.formatMessage({id: 'requiresInputStageLabel'})}
                        onTagClick={!_.isEmpty(search) ? matchClick(searchTargets.assistance)
                          : (hasNewAssistanceMessages ? () => openChipNotification(assistanceChipMessages) : undefined)}
                        tagTooltipId={!_.isEmpty(search) ? 'searchGoToMatch' : undefined}
                        tagColor={hasNewAssistanceMessages ? warningColor : undefined} tag={assistanceTag}
                        tagLabel={hasNewAssistanceMessages && _.isEmpty(search) ? 'new' : getTagLabel('open')} />
        )}
        <GmailTabItem icon={mobileLayout ? <NoteOutlined /> : undefined} label={intl.formatMessage({id: 'notesSection'})}
                      tagColor={hasNewNotesMessages ? warningColor : undefined}
                      onTagClick={!_.isEmpty(search) ? matchClick(searchTargets.notesEntry)
                        : (hasNewNotesMessages ? () => openChipNotification(inboxNewNotesMessages) : undefined)}
                      tagTooltipId={!_.isEmpty(search) ? 'searchGoToMatch' : undefined}
                      toolTipId='jobNotesToolTip' tagLabel={hasNewNotesMessages && _.isEmpty(search) ? 'new' : getTagLabel('total')}
                      tag={hasNewNotesMessages && _.isEmpty(search) ? `${numNewNotesMessages}` : 
                      (!_.isEmpty(search) && _.size(notesCommentsSearched) + _.size(capsuleNotes) > 0 ?
                        `${_.size(notesCommentsSearched) + _.size(capsuleNotes)}` : undefined)} />
      </GmailTabs>
      <div style={{paddingLeft: mobileLayout ? undefined : '2rem', paddingRight: mobileLayout ? undefined : '1rem'}}>
        <div style={{paddingBottom: '0.5rem'}} ref={refToTop}></div>
        {/* J-all-325: an add wizard opened inside the container replaces the tab body (Q-all-145, O-1).
           For add progress report and add approval the job title + description stay above it (T-all-2184). */}
        {inlineWizard && (
          <>
            {showJobHeaderAboveWizard && marketId && investibleId && (
              <div className={editClasses.container} style={{padding: '1rem', borderRadius: '8px',
                marginBottom: '1rem', backgroundColor: isDark ? DARK_TEXT_BACKGROUND_COLOR : 'white'}}>
                <Typography className={editClasses.title} variant="h3" component="h1">
                  {name}
                </Typography>
                <DescriptionOrDiff id={investibleId} description={description} showDiff={showDiff}
                                   backgroundColor={isDark ? DARK_TEXT_BACKGROUND_COLOR : 'white'} />
              </div>
            )}
            <InlineWizardHost inlineWizard={inlineWizard} onClose={closeInlineWizard} />
          </>
        )}
        {showOverview && (
          <>
            <div style={{display: 'flex', marginRight: mobileLayout ? undefined : '2rem'}}>
              <CardType
                isInAccepted={isInAccepted}
                isAssigned={isAssigned}
                className={classes.cardType}
                createdAt={mobileLayout ? undefined : createdAt}
                stageChangedAt={mobileLayout ? undefined : new Date(marketInfo.last_stage_change_date)}
                gravatar={<div style={{paddingLeft: '1rem'}}>
                  <GravatarAndName key={createdBy.id} email={createdBy.email}
                                   label={intl.formatMessage({ id: 'created_by' })}
                                   name={createdBy.name} typographyVariant="caption"
                                   avatarClassName={classes.smallGravatar}
                /></div>}
              />
              <div className={classes.editRow}>
                {/* T-all-2215: pencil edit button now shows on desktop too (mobile guard removed). */}
                {!inMarketArchives && isEditableByUser() && (
                  <div>
                    <EditMarketButton
                      labelId="edit"
                      marketId={marketId}
                      onClick={(event) => mySetBeingEdited(event)}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className={mobileLayout? undefined : classes.votingCardContent}
                 style={{display: 'flex', paddingLeft: mobileLayout ? '10px' : undefined, 
                   paddingBottom: mobileLayout ? '20px' : undefined, marginBottom: '2rem'}}>
              <div className={isEditableByUser() ? classes.fullWidthEditable :
                classes.fullWidth} onClick={(event) =>
                mySetBeingEdited(event)}>
                {lockedBy && myPresence.id !== lockedBy && isEditableByUser() && (
                  <Typography>
                    {intl.formatMessage({ id: "lockedBy" }, { x: lockedByName })}
                  </Typography>
                )}
                {marketId && investibleId && (
                  <div className={isEditableByUser() ? editClasses.containerEditable : editClasses.container} 
                    style={{padding: '1rem', borderRadius: '8px',
                    backgroundColor: isDark ? DARK_TEXT_BACKGROUND_COLOR : 'white'}}>
                    <Typography className={editClasses.title} variant="h3" component="h1">
                      {name}
                    </Typography>
                    <DescriptionOrDiff id={investibleId} description={description} showDiff={showDiff} backgroundColor={isDark ? DARK_TEXT_BACKGROUND_COLOR : 'white'} />
                  </div>
                )}
              </div>
            </div>
            {mobileLayout && (
              <div style={{marginBottom: '1rem'}}>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <h2 id="details" style={{marginBottom: 0, paddingBottom: 0, marginTop: 0, paddingTop: 0}}>
                    <FormattedMessage id="planningInvestibleOpenLabel" />
                  </h2>
                  <IconButton onClick={() => toggleDetails()} style={{marginBottom: 0,
                    paddingBottom: 0, marginTop: 0, paddingTop: 0}}>
                    <Tooltip key='toggleDetails'
                             title={<FormattedMessage id={`${detailsOpen ? 'closeDetails' : 'openDetails'}Tip`} />}>
                      {detailsOpen ? <ExpandLess fontSize='small' /> :
                        <ExpandMoreIcon fontSize='small' />}
                    </Tooltip>
                  </IconButton>
                </div>
                {detailsOpen && (
                  <div className={classes.mobileDetails}>
                    {investibleNav}
                  </div>
                )}
              </div>
            )}
            <DismissableText textId="planningInvestibleNotCollaboratorHelp" isLeft display={!isCollaborator} text={
              <div style={{ paddingBottom: '1rem' }}>
                    To ask a question, make a suggestion, or block this job go to the Assistance tab. To add information
                    edit the job description above.
            </div>}/>
            <CondensedTodos comments={todoCommentsSearched} investibleComments={investibleComments} showCommentAdd={showCommentAdd}
                            usePadding={!mobileLayout} hidden={hidden} hash={hash} maxWidth='95%' isSearch={!_.isEmpty(search)}
                            marketId={marketId} marketInfo={marketInfo} groupId={groupId} isDefaultOpen={!_.isEmpty(todoCommentsSearched)}/>
          </>
        )}
        <div style={showOverview ? {
                marginTop: '3rem',
                paddingBottom: mobileLayout ? undefined : '15vh',
                paddingLeft: mobileLayout ? undefined : '1rem',
                paddingRight: mobileLayout ? undefined : '1rem'
              } : { display: 'none' }}>
                <Approvals
                  key={investibleId}
                  displayVotingInput={displayVotingInput}
                  groupId={groupId}
                  hash={hash}
                  hidden={hidden}
                  investmentReasons={investmentReasonsSearched}
                  investibleId={investibleId}
                  isAssigned={isAssigned}
                  isInVoting={isInVoting}
                  market={market}
                  marketId={marketId}
                  marketPresences={marketPresences}
                  openInlineWizard={openInlineWizard}
                  search={search}
                  showExpiration={fullStage.has_expiration}
                  visible={showOverview}
                  yourPresence={yourPresence}
                />
              {showOverview && (
                <>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '3rem', marginBottom: '0.75rem' }}>
                  <h2 id="progress" style={{ marginBottom: 0, paddingBottom: 0, marginTop: 0, paddingTop: 0 }}>
                    <FormattedMessage id="reportsSectionLabel"/> {showCommentAdd && isAssigned && <TooltipIconButton id="newReport"
                        marginLeft='1rem'
                        onClick={() => openInlineWizard({ wizardType: JOB_COMMENT_WIZARD_TYPE, investibleId, marketId,
                          commentType: REPORT_TYPE })}
                        icon={hasJobComment(groupId, investibleId, REPORT_TYPE, unSentInvestibleComments) ? <EditIcon fontSize='small' /> : <AddIcon fontSize='small' />}
                        translationId="createNewStatus"
                      />}
                  </h2>
                  <IconButton id='reportsToggleId' onClick={() => toggleReports()} disabled={!_.isEmpty(search)} style={{
                      marginBottom: 0,
                      paddingBottom: 0, marginTop: 0, paddingTop: '4px'
                    }}>
                    <Tooltip key="toggleReports"
                             title={<FormattedMessage
                               id={`${reportsOpen ? 'closeReports' : 'openReports'}Tip`}/>}>
                      {reportsOpen ? <ExpandLess fontSize="small" /> :
                        <ExpandMoreIcon fontSize="small" />}
                    </Tooltip>
                  </IconButton>
                </div>
                {!isSingleUser && reportsOpen && (
                  <DismissableText textId="progressReportCommentHelp"
                                   display={isAssigned && _.isEmpty(reportsCommentsSearched)} isLeft
                                   text={<div>
                                     Your report asks the view members to review progress on this job.
                                   </div>
                                   }/>
                )}
                {(!showCommentAdd || !isAssigned) && _.isEmpty(reportsCommentsSearched) && reportsOpen && (
                  <Typography style={{ marginLeft: 'auto', marginRight: 'auto' }}
                              variant="body1">
                    No progress reports.
                  </Typography>
                )}
                {reportsOpen && (
                  <CommentBox
                    comments={reportsCommentsSearched.concat(replies)}
                    marketId={marketId}
                    isRequiresInput={isRequiresInput}
                    isInBlocking={isInBlocked}
                    fullStage={fullStage}
                    assigned={assigned}
                    formerStageId={formerStageId}
                    marketInfo={marketInfo}
                    investible={marketInvestible}
                    usePadding={false}
                  />
                )}
                </>
              )}
        </div>
        {!inlineWizard && sectionOpen !== 'descriptionVotingSection' && (
          <>
            {/* Vertical spacing lives on the wrapper so buttons and tabs space the same way as the
                view level sections (DiscussionSection, MarketTodos, Backlog) */}
            {showCommentAdd && !_.isEmpty(allowedCommentTypes) && (
              <div style={{ display: mobileLayout ? undefined : 'flex', marginLeft: '0.5rem', marginTop: '1rem',
                marginBottom: '1rem' }}>
                {allowedCommentTypes.map((allowedCommentType) => {
                  return (
                    <SpinningButton key={allowedCommentType} id={`new${allowedCommentType}`} className={wizardClasses.actionNext}
                                    icon={hasJobComment(groupId, investibleId, allowedCommentType, unSentInvestibleComments) ? EditIcon :
                                      AddIcon}
                                    iconColor="black"
                                    style={{ display: 'flex', marginRight: mobileLayout ? undefined : '2rem' }}
                                    toolTipId={
                      [TODO_TYPE, QUESTION_TYPE, SUGGEST_CHANGE_TYPE, ISSUE_TYPE, REPORT_TYPE].includes(allowedCommentType) ?
                                      `hotKey${allowedCommentType}`: undefined}
                                    variant="text" doSpin={false}
                                    onClick={() => openInlineWizard({ wizardType: JOB_COMMENT_WIZARD_TYPE, investibleId,
                                      marketId, commentType: allowedCommentType,
                                      notificationType: allowedCommentType === REPORT_TYPE ? 'BLUE' : undefined })}>
                      {intl.formatMessage({ id: `createNew${allowedCommentType}${mobileLayout ? 'Mobile' : ''}`})}
                    </SpinningButton>
                  );
                })}
                {sectionOpen === 'tasksSection' && !_.isEmpty(assigned) && showCommentAdd && !isInReview && (
                  <>
                    <SpinningButton id='allDone' className={wizardClasses.actionNext} iconColor="black"
                                    toolTipId='allDone' icon={DoneAll}
                                    doSpin={_.isEmpty(mustResolveComments)&&_.isEmpty(jobInboxMessages)}
                                    onClick={(event) => {
                                      // T-all-2439: with notifications still on the job, hitting All Done
                                      // offers to also clear them (this user's only - see Q-all-366)
                                      if (!_.isEmpty(jobInboxMessages)) {
                                        setAllDoneAnchorEl(event.currentTarget);
                                        return;
                                      }
                                      return moveJobToReviewable(false);
                                    }}
                                    style={{ display: 'flex', marginRight: mobileLayout ? undefined : '2rem' }}>
                      {intl.formatMessage({ id: 'allDoneButton'})}
                    </SpinningButton>
                    <Menu anchorEl={allDoneAnchorEl} open={Boolean(allDoneAnchorEl)}
                          onClose={() => setAllDoneAnchorEl(null)}>
                      <MenuItem id='allDoneClearNotifications' onClick={() => {
                        setAllDoneAnchorEl(null);
                        return moveJobToReviewable(true);
                      }}>
                        {intl.formatMessage({ id: 'allDoneClearNotifications' })}
                      </MenuItem>
                      <MenuItem id='allDoneMoveOnly' onClick={() => {
                        setAllDoneAnchorEl(null);
                        return moveJobToReviewable(false);
                      }}>
                        {intl.formatMessage({ id: 'allDoneMoveOnly' })}
                      </MenuItem>
                    </Menu>
                  </>
                )}
                {sectionOpen === 'tasksSection' && (
                  <div style={{marginTop: '0.5rem', fontWeight: 'bold'}}><Link
                    href={`${formInvestibleLink(marketId, investibleId)}#investibleCondensedTodos`}
                    color="primary"
                    onClick={(event) => {
                    preventDefaultAndProp(event);
                    navigate(history, `${formInvestibleLink(marketId, investibleId)}#investibleCondensedTodos`);
                  }}>Overview of tasks</Link></div>
                )}
              </div>
            )}
            {sectionOpen === 'assistanceSection' && (
              <GmailTabs
                value={assistanceTab}
                onChange={(event, value) => {
                  updatePageState({ assistanceTabIndex: value });
                }}
                indicatorColors={['#2F80ED', '#2F80ED', '#bdbdbd']}
                style={{ paddingBottom: '1rem', paddingTop: '1rem' }}>
                {/* T-all-2308: a sub-tab with new messages shows their count like the sections do.
                   B-all-480: Resolved only grows so its contents count does not display - only new
                   message and search match counts */}
                <GmailTabItem label={intl.formatMessage({ id: 'assistanceUnresponded' })} color='black'
                              toolTipId={isMac ? 'unrespondedNavigationMac' : 'unrespondedNavigation'}
                              onTagClick={!_.isEmpty(search) ? matchClick(searchTargets.assistanceTabs[0])
                                : (assistanceTabNewCounts[0] > 0 ? () => openChipNotification(assistanceTabNewMessageSets[0]) : undefined)}
                              tagTooltipId={!_.isEmpty(search) ? 'searchGoToMatch' : undefined}
                              tagColor={assistanceTabNewCounts[0] > 0 ? warningColor : undefined}
                              tagLabel={assistanceTabNewCounts[0] > 0 && _.isEmpty(search) ? 'new' :
                                getTagLabel('total')}
                              tag={assistanceTabNewCounts[0] > 0 && _.isEmpty(search) ?
                                `${assistanceTabNewCounts[0]}` : (_.size(unrespondedAssistanceComments) > 0 ?
                                  `${_.size(unrespondedAssistanceComments)}` : undefined)} />
                <GmailTabItem label={intl.formatMessage({ id: 'assistanceResponded' })} color='black'
                              onTagClick={!_.isEmpty(search) ? matchClick(searchTargets.assistanceTabs[1])
                                : (assistanceTabNewCounts[1] > 0 ? () => openChipNotification(assistanceTabNewMessageSets[1]) : undefined)}
                              tagTooltipId={!_.isEmpty(search) ? 'searchGoToMatch' : undefined}
                              tagColor={assistanceTabNewCounts[1] > 0 ? warningColor : undefined}
                              tagLabel={assistanceTabNewCounts[1] > 0 && _.isEmpty(search) ? 'new' :
                                getTagLabel('total')}
                              tag={assistanceTabNewCounts[1] > 0 && _.isEmpty(search) ?
                                `${assistanceTabNewCounts[1]}` : (_.size(respondedAssistanceComments) > 0 ?
                                  `${_.size(respondedAssistanceComments)}` : undefined)} />
                <GmailTabItem label={intl.formatMessage({ id: 'assistanceResolved' })} color='black'
                              onTagClick={!_.isEmpty(search) ? matchClick(searchTargets.assistanceTabs[2])
                                : (assistanceTabNewCounts[2] > 0 ? () => openChipNotification(assistanceTabNewMessageSets[2]) : undefined)}
                              tagTooltipId={!_.isEmpty(search) ? 'searchGoToMatch' : undefined}
                              tagColor={assistanceTabNewCounts[2] > 0 ? warningColor : undefined}
                              tagLabel={assistanceTabNewCounts[2] > 0 && _.isEmpty(search) ? 'new' :
                                getTagLabel('total')}
                              tag={assistanceTabNewCounts[2] > 0 && _.isEmpty(search) ?
                                `${assistanceTabNewCounts[2]}` : (!_.isEmpty(search) &&
                                  _.size(resolvedAssistanceComments) > 0 ?
                                  `${_.size(resolvedAssistanceComments)}` : undefined)} />
              </GmailTabs>
            )}
            {sectionOpen === 'assistanceSection' && assistanceTab > 0 && _.isEmpty(sectionComments) && (
              <Typography style={{marginTop: '2rem', maxWidth: '40rem', marginLeft: 'auto', marginRight: 'auto'}}
                          variant="body1">
                {intl.formatMessage({ id: assistanceTab === 1 ? 'assistanceResponded' : 'assistanceResolved' })} is empty.<br/><br/>
                {assistanceTab === 1 ?
                  'AI questions and suggestions where a human responded display here.' :
                  'Resolved questions, suggestions, and blockers display here.'}
              </Typography>
            )}
            <DismissableText textId="investibleCommentHelp"
                             display={_.isEmpty(sectionComments) && _.isEmpty(search) &&
                               !(sectionOpen === 'notesSection' && !_.isEmpty(capsuleNotes)) &&
                               (sectionOpen !== 'assistanceSection' || assistanceTab === 0)} noPad isLeft
                             text={
                               sectionOpen === 'assistanceSection' ? (
                                 <ul style={{ paddingLeft: 0, marginLeft: '1rem' }}>
                                   <li>Question - invites others to add voteable options for brainstorming</li>
                                   <li>Suggestion - others vote on this idea and then can be converted to a task</li>
                                   <li>Blocking issue - asks for help clearing the blocker</li>
                                 </ul>) : (sectionOpen === 'tasksSection' ? (<div>Tasks can be moved between jobs or converted to or from bugs.</div>) : 
                                 (<div>Notes can be added to at the task or job level. The AI's get_job only
                                   includes notes when include_all_resolved is passed or a note has a reply.</div>))
                             }/>
            {sectionOpen === 'notesSection' ? (
              <NotesTab
                notes={notesCommentsSearched}
                capsules={capsuleNotes}
                investibleComments={investibleComments}
                replies={replies}
                marketId={marketId}
                marketInfo={marketInfo}
                investible={marketInvestible}
                fullStage={fullStage}
                formerStageId={formerStageId}
                assigned={assigned}
                isRequiresInput={isRequiresInput}
                isInBlocking={isInBlocked}
              />
            ) : (
              <CommentBox
                comments={sectionComments.concat(replies)}
                investibleComments={investibleComments}
                marketId={marketId}
                isRequiresInput={isRequiresInput}
                isInBlocking={isInBlocked}
                fullStage={fullStage}
                assigned={assigned}
                showNotes={sectionOpen === 'tasksSection'}
                formerStageId={formerStageId}
                marketInfo={marketInfo}
                investible={marketInvestible}
                toggleCompression={sectionOpen === 'assistanceSection' ? toggleUseCompression : undefined}
                useCompression={sectionOpen === 'assistanceSection' ? getUseCompression : undefined}
                useInProgressSorting={sectionOpen === 'tasksSection'}
                oldestFirst={sectionOpen === 'assistanceSection' && assistanceTab === 0}
                simpleOrdering={sectionOpen === 'assistanceSection' && assistanceTab === 1}
                preserveOrder={sectionOpen === 'assistanceSection' && assistanceTab === 2}
              />
            )}
          </>
        )}
      </div>
      </InlineWizardLaunchContext.Provider>
    </Screen>
  );
}

PlanningInvestible.propTypes = {
  marketId: PropTypes.string.isRequired,
  marketInvestible: PropTypes.object.isRequired,
  marketPresences: PropTypes.arrayOf(PropTypes.object),
  investibleComments: PropTypes.arrayOf(PropTypes.object),
  investibles: PropTypes.arrayOf(PropTypes.object),
  investibleId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  inArchives: PropTypes.bool,
  hidden: PropTypes.bool
};

export function rejectInvestible (marketId, investibleId, marketInvestible, commentsState, commentsDispatch, invDispatch,
  diffDispatch, marketStagesState, marketPresencesDispatch) {
  const furtherWorkStage = getFurtherWorkStage(marketStagesState, marketId);
  const marketInfo = getMarketInfo(marketInvestible, marketId);
  const moveInfo = {
    marketId,
    investibleId,
    stageInfo: {
      current_stage_id: marketInfo.stage,
      stage_id: furtherWorkStage.id,
      open_for_investment: true
    },
  };
  return stageChangeInvestible(moveInfo)
    .then((newInv) => {
      onInvestibleStageChange(furtherWorkStage.id, newInv, investibleId, marketId, commentsState, commentsDispatch,
        invDispatch, diffDispatch, marketStagesState, undefined, furtherWorkStage, marketPresencesDispatch);
      pushMessage(OPERATION_HUB_CHANNEL, { event: STOP_OPERATION, id: 'reject' });
    });
}

export function Assignments(props) {
  const { marketPresences, classes, assigned, toolTipId, toggleIconButton, assignmentColumnMessageId,
    unaccceptedList, isLarge, unaccceptedMessage } = props;
  const intl = useIntl();
  const [themeMode] = React.useContext(ThemeModeContext);
  const isDark = themeMode === 'dark';
  const metaClasses = useMetaDataStyles();
  const safeAssigned = assigned || [];
  const presences = safeAssigned.map((userId) => {
    const presence = marketPresences.find(presence => presence.id === userId)
    if (!presence) {
      return { name : 'Removed', id: null }
    }
    return presence
  });
  // sort all removed last, then by name
  const sortedAssigned = _.sortBy(presences, [((presence) => presence.id? 0 : 1), (presence) => presence.name]);
  return (
    <>
      {assignmentColumnMessageId && (
        <div style={{display: 'flex', flexDirection: 'row'}}>
          {isLarge && (
            <Typography>
              {intl.formatMessage({ id: assignmentColumnMessageId })}
            </Typography>
          )}
          {!isLarge && (
            <b><FormattedMessage id={assignmentColumnMessageId} /></b>
          )}
          {toggleIconButton && (
            <div style={{marginTop: '-0.3rem'}}>
              <Tooltip placement='top' title={intl.formatMessage({ id: toolTipId })}>
                <IconButton size="small" id="addViewId" onClick={toggleIconButton}>
                  <AddIcon htmlColor={isDark ? DARK_ACTION_BUTTON_COLOR : 'black'} fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
      )}
      <div className={classes.assignmentFlexRow}>
        {sortedAssigned.map((presence, index) => {
          const showAsPlaceholder = presence.placeholder_type === PLACEHOLDER;
          const unacccepted = unaccceptedList?.includes(presence.id);
          const myClassName = showAsPlaceholder ? metaClasses.archived : metaClasses.normal;
          const name = fixName(presence.name);
          return (
            <div
              style={{
                display: 'flex', alignItems: 'center', paddingRight: '0.5rem',
                paddingTop: `${index > 0 ? '0.5rem' : 0}`
              }}
              key={`${presence.id}${toolTipId}`}
            >
              {!showAsPlaceholder && (
                <Gravatar email={presence.email} name={presence.name}
                          className={isLarge ? classes.largeGravatar : classes.smallGravatar}/>
              )}
              <div style={{display: 'flex'}}>
                <div>
                  <Typography component="li" className={myClassName}>
                    {name}
                  </Typography>
                  {unacccepted && (
                      <Tooltip
                        title={intl.formatMessage({ id: 'planningAcceptExplanation' })}
                      >
                        <Typography component="li" style={{fontSize: 12, background: '#ffdcdf',
                          paddingRight: '5px', paddingLeft: '5px'}}>
                          {intl.formatMessage({ id: 'planningUnacceptedLabel' })}
                        </Typography>
                      </Tooltip>
                  )}
                </div>
                {unaccceptedMessage && (
                  <NotificationMenuButton message={unaccceptedMessage} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default PlanningInvestible;
